import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { IoMailOutline, IoLockClosedOutline, IoEyeOutline, IoEyeOffOutline, IoArrowBack, IoShieldCheckmarkOutline } from 'react-icons/io5'
import toast from 'react-hot-toast'
import { authService } from '../services/api/auth'
import { pscAuthService } from '../services/api/psc'
import { useAuthStore } from '../stores'
import type { ApiResponse } from '../types'
import type { AuthResponse } from '../services/api/auth'
import logo from '../assets/medisync_logo.png'

export default function Login() {
  const navigate = useNavigate()
  const loginStore = useAuthStore(state => state.login)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [pscLoading, setPscLoading] = useState(false)

  const handlePscLogin = async () => {
    setPscLoading(true)
    try {
      const response = await pscAuthService.getAuthorizationUrl()
      if (!response.data?.authorizationUrl) throw new Error('Réponse serveur invalide')
      window.location.href = response.data.authorizationUrl
    } catch {
      toast.error('Impossible de contacter Pro Santé Connect')
      setPscLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim() || !password) { toast.error('Veuillez remplir tous les champs'); return }
    setLoading(true)
    try {
      const response = await authService.login({ email: email.trim(), password }) as ApiResponse<AuthResponse>
      const authData = response.data
      if (!authData?.user || !authData?.accessToken || !authData?.refreshToken) {
        toast.error('Réponse serveur invalide'); return
      }
      loginStore(authData.user, authData.accessToken, authData.refreshToken)
      toast.success('Connexion réussie')
      navigate('/app', { replace: true })
    } catch (err: unknown) {
      const raw = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : ''
      const message =
        /invalid credentials/i.test(raw)
          ? 'Email ou mot de passe incorrect'
          : raw || 'Erreur de connexion'
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-dark-50 flex'>
      {/* Panneau gauche — branding */}
      <div className='hidden lg:flex lg:w-1/2 bg-gradient-to-br from-navy-700 via-primary-600 to-primary-500 flex-col justify-between p-12'>
        <div className='flex items-center gap-3'>
          <img src={logo} alt='MediSync' className='h-12 w-auto bg-white rounded-xl p-1' />
        </div>

        <div>
          <h1 className='text-4xl font-bold text-white leading-tight mb-4'>
            La plateforme des<br />infirmiers modernes
          </h1>
          <p className='text-teal-100 text-lg leading-relaxed'>
            Gérez vos patients, planifiez vos tournées et communiquez en temps réel avec votre équipe.
          </p>
          <div className='mt-10 grid grid-cols-2 gap-4'>
            {[
              { label: 'Patients suivis', value: '2 400+' },
              { label: 'Infirmiers actifs', value: '380+' },
              { label: 'Tournées / jour', value: '1 200+' },
              { label: 'Disponibilité', value: '99.9%' },
            ].map(s => (
              <div key={s.label} className='bg-white/10 rounded-2xl p-4'>
                <div className='text-2xl font-bold text-white'>{s.value}</div>
                <div className='text-teal-200 text-sm mt-1'>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <p className='text-teal-300 text-sm'>© 2026 MediSync — Données sécurisées HDS</p>
      </div>

      {/* Panneau droit — formulaire */}
      <div className='flex-1 flex items-center justify-center px-8 py-12'>
        <div className='w-full max-w-md'>
          <div className='flex items-center gap-2 mb-10 lg:hidden'>
            <img src={logo} alt='MediSync' className='h-10 w-auto' />
          </div>

          <Link to='/' className='inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors mb-8'>
            <IoArrowBack size={14} />
            Retour à l'accueil
          </Link>

          <h2 className='text-2xl font-bold text-gray-900 dark:text-white mb-1'>Connexion</h2>
          <p className='text-gray-500 dark:text-gray-400 mb-8'>Accédez à votre espace infirmier</p>

          <form onSubmit={handleSubmit} className='space-y-5'>
            <div>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5'>
                Adresse email
              </label>
              <div className='relative'>
                <IoMailOutline className='absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-lg pointer-events-none' />
                <input
                  type='email'
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder='vous@exemple.com'
                  className='input-field input-with-icon-left'
                  autoComplete='email'
                  required
                />
              </div>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5'>
                Mot de passe
              </label>
              <div className='relative'>
                <IoLockClosedOutline className='absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-lg pointer-events-none' />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder='••••••••'
                  className='input-field input-with-icon-left pr-11'
                  autoComplete='current-password'
                  required
                />
                <button
                  type='button'
                  onClick={() => setShowPassword(v => !v)}
                  className='absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors'
                >
                  {showPassword ? <IoEyeOffOutline size={18} /> : <IoEyeOutline size={18} />}
                </button>
              </div>
            </div>

            <button type='submit' disabled={loading} className='btn-primary w-full py-3 text-base'>
              {loading ? 'Connexion en cours...' : 'Se connecter'}
            </button>
          </form>

          <div className='mt-5 relative'>
            <div className='absolute inset-0 flex items-center'>
              <div className='w-full border-t border-gray-200 dark:border-gray-700' />
            </div>
            <div className='relative flex justify-center text-xs uppercase'>
              <span className='bg-gray-50 dark:bg-dark-50 px-3 text-gray-400 font-medium tracking-wider'>
                ou
              </span>
            </div>
          </div>

          <button
            type='button'
            onClick={() => void handlePscLogin()}
            disabled={pscLoading}
            className='mt-4 w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl border-2 border-[#003189] bg-white dark:bg-dark-100 text-[#003189] dark:text-blue-400 dark:border-blue-500 font-semibold text-sm hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-all disabled:opacity-60'
          >
            <IoShieldCheckmarkOutline size={20} />
            {pscLoading ? 'Redirection...' : 'Se connecter avec Pro Santé Connect'}
          </button>
          <p className='mt-2 text-center text-xs text-gray-400'>
            Authentification via carte CPS ou e-CPS — réservé aux professionnels de santé
          </p>

          <p className='mt-6 text-center text-sm text-gray-500 dark:text-gray-400'>
            Pas encore de compte ?{' '}
            <Link to='/register' className='text-primary-600 dark:text-primary-400 font-semibold hover:underline'>
              Créer un compte
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
